import { html, TemplateResult, LitElement, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { msg, localized } from '@lit/localize';
import type { GalleryImage } from '../types/gallery.types.js';
import { buildGalleryRenderData, type RightSlotRenderData } from '../utils/gallery.utils.js';
import { galleryImageTemplate } from '../templates/gallery-image.template.js';

/**
 *
 */
@localized()
@customElement('der-tm-roundtrip-image-gallery')
export class ImageGallery extends LitElement {
    protected override createRenderRoot(): HTMLElement | DocumentFragment {
        return this; // Light DOM – render inside parent's Shadow DOM
    }

    @property({ type: Array })
    images: GalleryImage[] = [];

    @state()
    private _keyboardNavigation = false;

    /**
     * Tile keys ('left', 'right-0'..'right-3') whose <img> has fired `load`
     * (or `error` - a failed image still counts as "resolved", so its
     * per-tile skeleton doesn't spin forever). Used to progressively reveal
     * each tile independently, instead of only gating on "do we have image
     * URLs at all" one level up in RoundtripGallery/RoundtripStage, that
     * only covers the data-fetch gap, not the actual image byte download.
     */
    @state()
    private _loadedTileKeys: ReadonlySet<string> = new Set();

    /**
     * Prevents attaching duplicate load/error listeners to the same <img>
     * element across repeated `updated()` calls.
     */
    private readonly _observedImages = new WeakSet<HTMLImageElement>();

    private get renderData() {
        return buildGalleryRenderData(this.images);
    }

    private _dispatchOpenGallery = (index = 0): void => {
        this.dispatchEvent(new CustomEvent('open-gallery', { detail: { index } }));
    };

    private isPlaceholder(slot: RightSlotRenderData): slot is { placeholder: true } {
        return 'placeholder' in slot;
    }

    private _handleKeyDown = (event: KeyboardEvent): void => {
        if (event.key === 'Tab') {
            this._keyboardNavigation = true;
        }
    };

    private _handlePointerInteraction = (): void => {
        this._keyboardNavigation = false;
    };

    private _focusActionButtonInner = (event: FocusEvent): void => {
        const host = event.currentTarget as HTMLElement;
        const innerButton = host.shadowRoot?.querySelector<HTMLButtonElement>('button.c-action-button');
        if (!innerButton) return;
        if (host.shadowRoot?.activeElement === innerButton) return;

        queueMicrotask(() => {
            innerButton.focus();
        });
    };

    /**
     * Marks a tile as resolved (image loaded or failed) so its per-tile
     * skeleton overlay is removed.
     * @param key - Stable tile identifier ('left', 'right-0', ...)
     */
    private _markTileLoaded = (key: string): void => {
        if (this._loadedTileKeys.has(key)) return;
        const next = new Set(this._loadedTileKeys);
        next.add(key);
        this._loadedTileKeys = next;
    };

    /**
     * Attaches load/error tracking to a tile's <img> element exactly once.
     * Handles the common case where the image is already cached and
     * `complete` by the time we look at it (no load event would fire then).
     * @param key - Stable tile identifier
     * @param img - The real <img> element rendered by galleryImageTemplate, if present
     */
    private _trackTileImage(key: string, img: HTMLImageElement | null | undefined): void {
        if (!img) return;

        if (img.complete) {
            this._markTileLoaded(key);
            return;
        }

        if (this._observedImages.has(img)) {
            return;
        }
        this._observedImages.add(img);

        img.addEventListener('load', () => this._markTileLoaded(key), { once: true });
        img.addEventListener('error', () => this._markTileLoaded(key), { once: true });
    }

    /**
     * Re-runs tile image tracking after every render, and resets resolved
     * state when the `images` input itself changes (a new image set should
     * get its own fresh per-tile loading pass).
     * @param changedProperties - Properties changed since the previous update cycle
     */
    override updated(changedProperties: Map<string, unknown>): void {
        if (changedProperties.has('images')) {
            this._loadedTileKeys = new Set();
        }

        const leftImg = this.querySelector<HTMLImageElement>('.image-gallery-left-image img');
        this._trackTileImage('left', leftImg);

        this.querySelectorAll<HTMLImageElement>('.image-gallery-right-image img').forEach((img, index) => {
            this._trackTileImage(`right-${index}`, img);
        });
    }

    /**
     * Track global keyboard/pointer modality so Safari shows focus ring while tabbing.
     */
    override connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('keydown', this._handleKeyDown, true);
        window.addEventListener('pointerdown', this._handlePointerInteraction, true);
    }

    /**
     * Remove global modality listeners when the component is detached.
     */
    override disconnectedCallback(): void {
        window.removeEventListener('keydown', this._handleKeyDown, true);
        window.removeEventListener('pointerdown', this._handlePointerInteraction, true);
        super.disconnectedCallback();
    }

    protected render(): TemplateResult {
        const data = this.renderData;
        if (!data) return html``;

        // 5+ groups use the 5-image teaser layout (large left image + 4 tiles on the right).
        const teaserLayoutImageCount = Math.min(data.imageCount, 5);

        return html`
            <div
                class="image-gallery-wrapper image-count-${teaserLayoutImageCount} ${this._keyboardNavigation
                    ? 'keyboard-nav'
                    : ''}"
            >
                <div class="image-gallery-left">
                    <div class="image-gallery-left-image">
                        <button
                            type="button"
                            tabindex="0"
                            @click=${() => this._dispatchOpenGallery(0)}
                            aria-label=${msg('Open image gallery', {
                                desc: 'Button label to open the image gallery overlay',
                            })}
                        >
                            ${galleryImageTemplate(data.left, 'eager')}
                        </button>
                        ${!this._loadedTileKeys.has('left')
                            ? html`<div class="der-tm-gallery-skeleton-overlay" aria-hidden="true"></div>`
                            : nothing}
                        <div class="image-gallery-left-action">
                            <der-ui-action-button
                                icon="systemImageGallery"
                                type="button"
                                tabindex="0"
                                @focus=${this._focusActionButtonInner}
                                @click=${() => this._dispatchOpenGallery(0)}
                            >
                                ${msg('Image gallery', {
                                    desc: 'Button label to open the image gallery',
                                })}
                            </der-ui-action-button>
                        </div>
                    </div>
                </div>
                <div class="image-gallery-right">
                    ${data.right.map((slot, index) => {
                        if (this.isPlaceholder(slot)) {
                            return html`
                                <div
                                    class="image-gallery-right-image image-gallery-placeholder"
                                    aria-hidden="true"
                                ></div>
                            `;
                        }

                        const key = `right-${index}`;

                        return html`
                            <div class="image-gallery-right-image">
                                <button
                                    type="button"
                                    tabindex="0"
                                    @click=${() => this._dispatchOpenGallery(index + 1)}
                                    aria-label=${msg('Open image gallery', {
                                        desc: 'Button label to open the image gallery overlay',
                                    })}
                                >
                                    ${galleryImageTemplate(slot, slot.loading)}
                                </button>
                                ${!this._loadedTileKeys.has(key)
                                    ? html`<div class="der-tm-gallery-skeleton-overlay" aria-hidden="true"></div>`
                                    : nothing}
                            </div>
                        `;
                    })}
                </div>
            </div>
        `;
    }
}
